import { useState, useEffect, useRef } from 'react';
import { directusService } from '@/services/directus';

interface PaymentTrackingOptions {
  paymentIntentId: string;
  apiKey: string;
  onSuccess: () => void;
  onError?: (error: Error) => void;
}

export function usePaymentTracking({ paymentIntentId, apiKey, onSuccess, onError }: PaymentTrackingOptions) {
  const [trackingStatus, setTrackingStatus] = useState<string>('Connexion en cours...');
  const [isPollingMode, setIsPollingMode] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const wsConnectionTimerRef = useRef<number | null>(null);
  const pollingAttemptsRef = useRef(0);

  const MAX_POLLING_ATTEMPTS = 36; // 3 minutes at 5 seconds interval
  const POLLING_INTERVAL = 5000; // 5 seconds
  const WS_CONNECTION_TIMEOUT = 30000; // 30 seconds

  useEffect(() => {
    if (!paymentIntentId || isCompleted) return;

    console.debug('🔄 Starting payment tracking for intent:', paymentIntentId);
    startWebSocketTracking();

    return () => {
      cleanup();
    };
  }, [paymentIntentId, isCompleted]);

  const cleanup = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    if (wsConnectionTimerRef.current) {
      clearTimeout(wsConnectionTimerRef.current);
      wsConnectionTimerRef.current = null;
    }
  };

  const startWebSocketTracking = () => {
    setTrackingStatus('Connexion au serveur...');

    // Set timeout to fallback to polling if WebSocket doesn't connect
    wsConnectionTimerRef.current = window.setTimeout(() => {
      if (!isCompleted) {
        console.debug('⚠️ WebSocket timeout - falling back to polling');
        fallbackToPolling();
      }
    }, WS_CONNECTION_TIMEOUT);

    try {
      const ws = new WebSocket('wss://ftx-pay.futurix.xyz/websocket');
      wsRef.current = ws;

      ws.onopen = () => {
        console.debug('✅ WebSocket connected, authenticating...');
        if (wsConnectionTimerRef.current) {
          clearTimeout(wsConnectionTimerRef.current);
          wsConnectionTimerRef.current = null;
        }

        setTrackingStatus('Connecté - En attente du paiement...');

        // Authenticate
        ws.send(JSON.stringify({
          type: 'auth',
          access_token: apiKey,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'auth' && data.status === 'ok') {
            console.debug('🔐 WebSocket authenticated successfully');
            // Subscribe to session updates
            ws.send(JSON.stringify({
              type: 'subscribe',
              collection: 'session',
              query: {
                fields: ['status', 'id'],
                filter: {
                  id: { _eq: paymentIntentId }
                }
              },
            }));
          }

          if (data.type === 'subscription' && data.event === 'update') {
            console.debug('💳 Payment status update received');
            if (data.data && data.data[0] && data.data[0].status === false) {
              console.debug('✅ Payment completed via WebSocket');
              setIsCompleted(true);
              setTrackingStatus('Paiement confirmé !');
              cleanup();
              onSuccess();
            }
          }

          if (data.type === 'ping') {
            ws.send(JSON.stringify({ type: 'pong' }));
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        if (!isPollingMode && !isCompleted) {
          fallbackToPolling();
        }
      };

      ws.onclose = () => {
        console.debug('🔌 WebSocket connection closed');
        if (!isCompleted && !isPollingMode) {
          fallbackToPolling();
        }
      };
    } catch (error) {
      console.error('❌ Error setting up WebSocket:', error);
      fallbackToPolling();
    }
  };

  const fallbackToPolling = () => {
    if (isPollingMode || isCompleted) return;

    console.debug('🔄 Switching to HTTP polling mode');
    setIsPollingMode(true);
    setTrackingStatus('Vérification du paiement...');

    // Cancel WebSocket
    cleanup();

    // Start polling
    pollingAttemptsRef.current = 0;
    checkPaymentStatus();

    pollingTimerRef.current = window.setInterval(() => {
      if (isCompleted) {
        if (pollingTimerRef.current) {
          clearInterval(pollingTimerRef.current);
        }
        return;
      }

      pollingAttemptsRef.current++;

      if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
        console.debug('⏱️ Polling timeout reached');
        if (pollingTimerRef.current) {
          clearInterval(pollingTimerRef.current);
        }
        setTrackingStatus("Délai d'attente dépassé");
        onError?.(new Error('Payment tracking timeout'));
        return;
      }

      setTrackingStatus(`Vérification ${pollingAttemptsRef.current}/${MAX_POLLING_ATTEMPTS}...`);
      checkPaymentStatus();
    }, POLLING_INTERVAL);
  };

  const checkPaymentStatus = async () => {
    try {
      console.debug(`📡 Polling attempt ${pollingAttemptsRef.current}/${MAX_POLLING_ATTEMPTS}`);
      const session = await directusService.checkPaymentStatus(paymentIntentId);

      console.debug('📊 Payment status from API:', session.status);

      if (session.status === false || session.callBackResponse != null) {
        console.debug('✅ Payment completed via polling');
        setIsCompleted(true);
        setTrackingStatus('Paiement confirmé !');
        cleanup();
        onSuccess();
      }
    } catch (error) {
      console.error('❌ Polling error:', error);
      // Continue polling on error
    }
  };

  return {
    trackingStatus,
    isPollingMode,
    isCompleted,
    pollingAttempts: pollingAttemptsRef.current,
  };
}
